/**
 * SiteUP CRM: "Exibicao do card" do funil, preferencia POR USUARIO.
 *
 * O card ja tinha um mecanismo de visibilidade de campos (as refs `show*Field`
 * de KanbanItem.vue, gravadas em `localStorage.kanban_items_visibility` por id
 * de item, pelo modal "Personalizar campos"). Esta fatia NAO cria um segundo
 * mecanismo: ela acrescenta uma camada de usuario por cima, guardada nas UI
 * settings (`kanban_card_display`), e combina as duas.
 *
 * A regra da combinacao e uma so: a preferencia do usuario apenas ESCONDE. O
 * que a configuracao do card ja escondeu continua escondido, mesmo que o
 * usuario ligue o bloco. Por isso a combinacao e um E logico, nunca um OU.
 *
 * Helper puro: so entra dado, so sai dado. Sem Vue, sem store, sem i18n.
 */

/** Chave das UI settings (por usuario, por conta). */
export const CARD_DISPLAY_UI_SETTINGS_KEY = 'kanban_card_display';

/**
 * Blocos que o usuario liga e desliga, na ordem em que aparecem no popover.
 * A ordem segue a leitura do card, de cima para baixo.
 */
export const CARD_DISPLAY_KEYS = [
  'description',
  'labels',
  'conversationPreview',
  'nextStep',
  'value',
  'assignee',
  'timeInStage',
];

/** Padrao: tudo ligado. Quem nunca abriu o popover ve o card inteiro. */
export const DEFAULT_CARD_DISPLAY = Object.freeze(
  CARD_DISPLAY_KEYS.reduce((acc, key) => ({ ...acc, [key]: true }), {})
);

/**
 * De cada bloco para a ref `show*Field` que o modal "Personalizar campos" ja
 * controlava. `nextStep` nao tem par: e um bloco novo, entao a configuracao do
 * card nunca o esconde e so a preferencia do usuario decide.
 */
export const CARD_DISPLAY_FIELD_MAP = Object.freeze({
  description: 'showDescriptionField',
  labels: 'showLabelsField',
  conversationPreview: 'showConversationField',
  nextStep: null,
  value: 'showValueField',
  assignee: 'showAgentField',
  timeInStage: 'showMetadataField',
});

/**
 * Le o que veio das UI settings sem confiar nele: chave desconhecida cai fora,
 * valor que nao e booleano volta para o padrao (ligado), e `null`/lixo devolve
 * o padrao inteiro. UI settings sao um JSON livre no servidor, entao o card nao
 * pode quebrar por causa de um valor estranho.
 *
 * @param {Object|null|undefined} raw valor cru de `kanban_card_display`.
 * @returns {Object} objeto com TODAS as chaves de CARD_DISPLAY_KEYS, booleanas.
 */
export const normalizeCardDisplay = raw => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...DEFAULT_CARD_DISPLAY };
  }
  return CARD_DISPLAY_KEYS.reduce((acc, key) => {
    acc[key] = typeof raw[key] === 'boolean' ? raw[key] : true;
    return acc;
  }, {});
};

/**
 * Combina a configuracao do card (o que pode ser mostrado) com a preferencia do
 * usuario (o que ele quer ver). Esconder vence mostrar.
 *
 * @param {Object} allowed mapa parcial `{bloco: boolean}` vindo das refs
 *   `show*Field`. Bloco ausente conta como permitido.
 * @param {Object} preference preferencia do usuario, ja normalizada ou crua.
 * @returns {Object} mapa com todas as chaves, booleanas.
 */
export const resolveCardDisplay = (allowed = {}, preference = {}) => {
  const userPreference = normalizeCardDisplay(preference);
  const allowedMap =
    allowed && typeof allowed === 'object' && !Array.isArray(allowed)
      ? allowed
      : {};

  return CARD_DISPLAY_KEYS.reduce((acc, key) => {
    const isAllowed = allowedMap[key] !== false;
    acc[key] = isAllowed && userPreference[key];
    return acc;
  }, {});
};

export default resolveCardDisplay;
